import React, { useState, useEffect, useMemo } from 'react';
import {
  useFeathers,
  mergeReferencesWithDraft,
  researcherLevelPermissions
} from '../../app/util';

import {
  ReferenceEditor,
  ReviewProgress,
  TaskPage,
  EditCandidateProfile,
  mapDraftToServerInputs,
  newDraftAfterError,
  TimeTracker,
} from '../../components';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { Alert, AlertTitle, Button, Typography } from '@mui/material';
import { SCRAPED_CONTENT_METADATA_FIELDS } from '../../app/feathers/scraped-content/scraped-content';

const scaffoldIssuesFromRace = (draft, race) => {
  const hasExistingIssues = Array.isArray(draft?.issues) && draft.issues.length > 0;
  if (hasExistingIssues) return draft;

  const issuesToCover = race?.issuesToCover || [];
  if (issuesToCover.length === 0) return draft;

  return {
    ...draft,
    issues: issuesToCover.map((issue) => ({
      key: issue.key,
      title: issue.name,
      complete: false,
      stances: [],
      sources: [],
      isTopPriority: false,
      policyTerms: [],
    })),
  };
};

const ResearchCandidate = () => {
  const feathers = useFeathers();
  const user = useSelector(state => state.user);
  const [task, setTask] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [race, setRace] = useState(null);
  const [taskPageSaving, setTaskPageSaving] = useState(false);
  const [completionData, setCompletionData] = useState(null)
  const [stagedUpdatesToReferences, setStagedUpdatesToReferences] = useState(null);
  const userIsPublisher = (user?.permissions || []).some((p) => ['publisher', 'editor', 'super-admin'].includes(p))
  const userHasPublisherLevel = (
    user.publisherLevel && 
    researcherLevelPermissions[user.publisherLevel].includes(candidate?.priorityLevel)
  );
  const publishPerms = userIsPublisher || userHasPublisherLevel;
  const [scrapedContent, setScrapedContent] = useState([]);
  const [trackedTimesByArea, setTrackedTimesByArea] = useState({});
  const [reset, setReset] = useState(0);

  /* Candidate being actively edited */
  const [candidateEdit, setCandidateEdit] = useState({});

  // Apply $scrapedContentChanges to scrapedContent to create scrapedContentMapped
  const scrapedContentMapped = useMemo(() => {
    if (!scrapedContent || !candidateEdit?.$scrapedContentChanges) {
      return scrapedContent;
    }

    const changesMap = new Map(
      candidateEdit.$scrapedContentChanges.map(change => [
        change.scrapedContentId,
        change.changes
      ])
    );

    // Filter out deleted items and apply type changes
    return scrapedContent
      .filter(sc => {
        const changes = changesMap.get(sc._id);
        // Filter out items marked for deletion
        return !changes || changes.delete !== true;
      })
      .map(sc => {
        const changes = changesMap.get(sc._id);
        if (!changes || changes.type === undefined) {
          return sc;
        }

        // Apply the changes to create a new scrapedContent object
        return {
          ...sc,
          type: changes.type
        };
      });
  }, [scrapedContent, candidateEdit?.$scrapedContentChanges]);

  const handleScrapedContentSaved = (updated) => {
    setScrapedContent(existing => existing.map(item => (
      item._id === updated._id
        ? {
            ...item,
            status: updated.status,
            error: updated.error,
            addMethod: updated.addMethod,
            isMostRecent: updated.isMostRecent,
            updatedAt: updated.updatedAt,
          }
        : item
    )));
  };
  
  useEffect(() => {
    if (stagedUpdatesToReferences && candidateEdit) {
      setStagedUpdatesToReferences(null);
      setCandidateEdit(
        mergeReferencesWithDraft(
          stagedUpdatesToReferences,
          candidateEdit
        )
      );
    }
  }, [candidateEdit, stagedUpdatesToReferences, task]);

  const loadScrapedContent = async (candidateId) => {
    try {
      const scrapedContentCount = await feathers.getService('scraped-content').find({
        query: {
          candidateId,
          $limit: 0,
        }
      });
      const numChunks = Math.ceil(scrapedContentCount.total / 10);
      const chunks = Array.from({ length: numChunks }, (_, i) => i);
      const scrapedContent = (await Promise.all(chunks.map(i => {
        return feathers.getService('scraped-content').find({
          query: {
            candidateId,
            $limit: 10,
            $skip: i * 10,
            $sort: { createdAt: -1 },
            $select: SCRAPED_CONTENT_METADATA_FIELDS,
          }
        })
      }))).map(res => res.data).flat();

      setScrapedContent(scrapedContent);
    } catch(err) {
      console.log(`Failed to load scraped content: `, err);
    }
  }
  
  const onCheckoutTask = async (task, loadNextTask) => {
    setReset(0);
    setCandidate(null);
    setCandidateEdit(null);
    setRace(null);
    setTask(task);
    let candidate;
    try {
      candidate = await feathers.getService('candidates').get(task.details.candidate);
    } catch(err) {
      if(err.name === 'NotFound') {
        // bad candidate, we need to cancel this task
        console.log('Candidate not found')
        await feathers.getService('research-tasks').patch(task._id, {
          $cancelTask: true
        });
        setTask(null);
        loadNextTask()
        return
      } else {
        throw err;
      }
    }
    setCandidate(candidate);
    const { updatedAt: holdingDataUpdatedAt, ...holdingData } = task?.holdingData || {};
    // seed inputs with most recently updated data from holdingData or candidate record
    const candidateDraft = (
      ((holdingData && holdingDataUpdatedAt > candidate?.updatedAt)
        ? holdingData
        : candidate?.stagingDraft) 
      || {}
    )
    const candidateDraftWithRemovedContent = {
      ...candidateDraft,
      removedAutoContent: candidateDraft.removedAutoContent || candidate.removedAutoContent,
    };

    const raceId = candidate.race;

    const [_, loadedRace] = await Promise.all([
      loadScrapedContent(candidate._id),
      (async () => {
        console.log('raceId', raceId)
        if (!raceId) return null;

        const race = await feathers.getService('races').get(raceId);
        setRace(race);

        console.log('race', race)
        return race;
      })()
    ]);

    setCandidateEdit(scaffoldIssuesFromRace(candidateDraftWithRemovedContent, loadedRace));
  }

  useEffect(() => {
    if (candidate && candidateEdit) {
      const base = mapDraftToServerInputs(candidateEdit);
      const withIssuesToCoverAdditional = candidateEdit.issuesToCoverAdditional
        ? { issuesToCoverAdditional: candidateEdit.issuesToCoverAdditional }
        : {};
      setCompletionData({
        ...base,
        ...withIssuesToCoverAdditional,
      });
    }
  }, [candidate, candidateEdit, task])
  
  const onCompleteError = (e) => { setCandidate((c) => newDraftAfterError(c, e)) }; 

  // Handler for receiving tracked times from EditCandidateProfile
  const handleTrackedTimesUpdate = (times) => {
    setTrackedTimesByArea(times);
  };

  return (
    <TaskPage
      task={task}
      taskType='review-candidate'
      onCheckoutTask={onCheckoutTask}
      completionData={completionData
        ? {
          ...completionData,
          trackedTimesByArea: trackedTimesByArea
        }
        : completionData
      }
      holdingData={candidateEdit}
      onChangeSaving={(saving) => setTaskPageSaving(saving)}
      onCompleteError={onCompleteError}
      completeButtonLabel={publishPerms ? 'Complete and Publish' : 'Complete'}
      disableTimeTracker
      sidePaneConfig={([
        {
          title: 'Priority',
          content: (
            <>
              <TimeTracker
                seedTask={task}
                priorityLevel={candidate?.priorityLevel}
              />
              {
                publishPerms && (
                  <Typography variant='body2' style={{ marginTop: '16px' }}>
                    You have permissions to publish this candidate directly.
                  </Typography>
                )
              }
            </>
          ),
          initiallyExpanded: true,
        },
        (!!candidate?.coveragePlan &&
        {
          title: 'Progress',
          content: (
            <ReviewProgress
              coveragePlan={candidate?.coveragePlan}
              candidate={candidateEdit}
              key={`Progress`}
            />
          ),
          initiallyExpanded: true,
        }),
        (!!candidateEdit?.references &&
        {
          title: 'Information sources',
          content: (
            <ReferenceEditor
              references={candidateEdit?.references}
              onChangeActiveDraft={(update, scrapedContentChanges) => setCandidateEdit({
                ...candidateEdit, 
                ...update,
                ...(scrapedContentChanges ? { $scrapedContentChanges: scrapedContentChanges } : {})
              })}
              onChangeReferences={setStagedUpdatesToReferences}
              key="Information sources"
              disabled={taskPageSaving} // being set in child via handleParentSaving
              activeDraft={candidateEdit}
              scrapedContent={scrapedContentMapped}
              onScrapedContentSaved={handleScrapedContentSaved}
            />
          ),
          initiallyExpanded: true,
        }),
      ])}
    >
      <WrapperInner>
        {
          candidate?.withdrawn && (
            <Alert
              severity='warning'
              style={{ marginBottom: '24px' }}
              action={
                <Button
                  color='inherit'
                  size='small'
                  onClick={() => setReset(currentReset => currentReset + 1)}
                  disabled={!candidateEdit || taskPageSaving}
                >
                  Reset
                </Button>
              }
            >
              <AlertTitle>Candidate is withdrawn</AlertTitle>
              Mark each field as unavailable and then publish the candidate.
            </Alert>
          )
        }
        <EditCandidateProfile
          value={candidateEdit}
          onChange={setCandidateEdit}
          onChangeIssue={(newIssue, issueKey) => {
            setCandidateEdit(existingDraft => {
              const existingDraftIssues = existingDraft.issues || [];
              const newIssues = existingDraftIssues.map((issue, i) => {
                if(issue.key === issueKey) {
                  return newIssue
                }
                return issue
              })

              return {
                ...existingDraft,
                issues: newIssues
              }
            })
          }}
          candidateId={candidate?._id}
          photoOptions={candidate?.photoOptions}
          race={race}
          disabled={taskPageSaving} // being set in child
          electionKey={
            candidate?.election?.key || candidate?.election
          }
          reset={reset}
          onTrackedTimesUpdate={handleTrackedTimesUpdate}
        />
      </WrapperInner>
    </TaskPage>
  );
};

const WrapperInner = styled.div`
  padding: 0 24px;
`
export default ResearchCandidate;
